import type { IProps, FormValues, PhraseSearchFormValues } from "./phrase-search.types";
import {
    faPlus,
    faQuestionCircle,
    faQuoteRight,
    faTrash,
    faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";

import { Button } from "../../button";
import { Text } from "../../text";
import { Content } from "../../content";
import { Group } from "../../group";
import { ActionIcon } from "../../action-icon";
import { Icon } from "../../icon";
import styles from "./phrase-search.module.scss";
import { Popover } from "../../popover";
import { FunctionComponent, useMemo, useState } from "react";
import { Select } from "../../select";
import { Formik, FieldArray, Form } from "formik";
import { SelectSimilarity } from "../../select-similarity/select-similarity";
import * as Yup from "yup";
import { SimilarityTypes, SimilarityValues } from "../../select-similarity/select-similarity.constants";
import { EMPTY_FIELD_GROUP, EXACT_MESSAGE_SOURCES } from "./phrase-search.constants";
import { Input } from "../../input";
import { Grid } from "../../grid";
import { DropdownIcon } from "../../dropdown-icon";
import { Stack } from "../../stack";
import { Tooltip } from "../../tooltip";
import { List } from "../../list";

const PhraseSearch: FunctionComponent<IProps> = ({
    sourceOptions,
    initialValues,
    onSubmit,
    name,
    onReset,
    placeholder,
    hasLimitedDateRange = false,
    activable,
}) => {
    const [opened, setOpened] = useState(false);

    const formInitialValues = useMemo(
        () => (initialValues && initialValues.length > 0 ? initialValues : EMPTY_FIELD_GROUP),
        [initialValues]
    );

    const validSelections = useMemo(() => {
        return (initialValues || []).filter(
            (item: PhraseSearchFormValues) => item.sentence?.trim() && item.source?.length > 0
        ).length;
    }, [initialValues]);

    const inputLabel = useMemo(() => {
        if (validSelections === 0) return placeholder || "Select phrase";
        if (validSelections === 1) return initialValues[0].sentence;

        return `${validSelections} phrases selected`;
    }, [initialValues, validSelections, placeholder]);

    return (
        <Popover width={900} position="bottom-start" shadow="md" opened={opened} onDismiss={() => setOpened(false)}>
            <Popover.Target>
                <Input
                    active={activable && validSelections > 0}
                    leftSection={<Icon icon={faQuoteRight} size="sm" />}
                    rightSection={<DropdownIcon isOpen={opened} />}
                    component="button"
                    pointer
                    type="button"
                    onClick={() => setOpened(!opened)}
                    data-testid="search-phrase-input-filter"
                    data-is-placeholder={validSelections === 0}
                >
                    {inputLabel}
                </Input>
            </Popover.Target>
            <Popover.Dropdown>
                <Formik<FormValues>
                    initialValues={{ [name]: formInitialValues }}
                    onSubmit={(values) => {
                        onSubmit(values);
                        setOpened(false);
                    }}
                    validateOnMount
                    validationSchema={Yup.object({
                        [name]: Yup.array().of(
                            Yup.object({
                                source: Yup.array().min(1, "Required"),
                                similarityThreshold: Yup.number().nullable().required("Required"),
                                sentence: Yup.mixed().when(["similarityThreshold"], (similarityThreshold: number) => {
                                    if (similarityThreshold !== SimilarityValues.EXACT_WORD / 100) {
                                        return Yup.string()
                                            .matches(/\S+\s+\S+\s\S/i, "Must be at least 3 words")
                                            .required("Required");
                                    }

                                    return Yup.string().trim().required("Required");
                                }),
                            })
                        ),
                    })}
                >
                    {({ values, setFieldValue, resetForm, isValid, validateForm, errors }) => {
                        const hasExactSimilarity = values[name].every(
                            (fieldRow) => fieldRow.similarityThreshold === SimilarityValues.EXACT_WORD / 100
                        );

                        const similarValuesDisabled = Array.from(
                            new Set(
                                values[name].flatMap((fieldValues) =>
                                    fieldValues.source.flatMap((messageSource) => {
                                        if (EXACT_MESSAGE_SOURCES.includes(messageSource)) {
                                            return [
                                                SimilarityTypes.CLOSE_MATCH,
                                                SimilarityTypes.VERY_SIMILAR,
                                                SimilarityTypes.SIMILAR,
                                                SimilarityTypes.APPROXIMATE,
                                                SimilarityTypes.CUSTOM,
                                            ];
                                        }
                                        return [];
                                    })
                                )
                            )
                        );
                        return (
                            <Form role="form">
                                <Content>
                                    <Content.Body>
                                        <Grid gutter="md" align="end">
                                            <Grid.Col span={5}>
                                                <Text variant="medium" size="md">
                                                    Find word or phrase
                                                </Text>
                                            </Grid.Col>
                                            <Grid.Col span={3}>
                                                <Text variant="medium" size="md">
                                                    in
                                                </Text>
                                            </Grid.Col>
                                            <Grid.Col span={3}>
                                                <Text variant="medium" size="md">
                                                    that are
                                                </Text>
                                            </Grid.Col>
                                            <Grid.Col span={1}></Grid.Col>
                                        </Grid>

                                        <FieldArray name={name}>
                                            {({ push, remove }) => (
                                                <>
                                                    {values[name].map(
                                                        (option: PhraseSearchFormValues, index: number) => (
                                                            <Stack key={index} gap="md" my="md">
                                                                <Grid gutter="md" align="end">
                                                                    <Grid.Col
                                                                        className={styles["row-group-item"]}
                                                                        span={5}
                                                                    >
                                                                        <Input.Wrapper
                                                                            error={
                                                                                <span>
                                                                                    {
                                                                                        (errors as any)?.[name]?.[index]
                                                                                            ?.sentence
                                                                                    }
                                                                                </span>
                                                                            }
                                                                        >
                                                                            <Input
                                                                                name={`${name}.${index}.sentence`}
                                                                                value={option.sentence}
                                                                                variant="filled"
                                                                                autoFocus
                                                                                onChange={(e) => {
                                                                                    setFieldValue(
                                                                                        `${name}.${index}.sentence`,
                                                                                        e.target.value
                                                                                    );
                                                                                }}
                                                                                placeholder="Word or Phrase"
                                                                                rightSectionPointerEvents="all"
                                                                                rightSection={
                                                                                    index === 0 && (
                                                                                        <Tooltip
                                                                                            label={
                                                                                                <Stack>
                                                                                                    <Text size="sm">
                                                                                                        Include multiple
                                                                                                        words and/or
                                                                                                        phrases using
                                                                                                        quotation marks:
                                                                                                    </Text>
                                                                                                    <List
                                                                                                        listStyleType="disc"
                                                                                                        pl="xs"
                                                                                                    >
                                                                                                        <List.Item>
                                                                                                            <Text size="sm">
                                                                                                                &quot;wonderful&quot;
                                                                                                                and
                                                                                                                &quot;service&quot;
                                                                                                                finds
                                                                                                                interactions
                                                                                                                that
                                                                                                                contain{" "}
                                                                                                                <Text
                                                                                                                    span
                                                                                                                    fs="italic"
                                                                                                                >
                                                                                                                    both{" "}
                                                                                                                </Text>
                                                                                                                terms.
                                                                                                            </Text>
                                                                                                        </List.Item>
                                                                                                        <List.Item>
                                                                                                            <Text size="sm">
                                                                                                                &quot;wonderful&quot;
                                                                                                                or
                                                                                                                &quot;service&quot;
                                                                                                                finds
                                                                                                                interactions
                                                                                                                that
                                                                                                                contain{" "}
                                                                                                                <Text
                                                                                                                    span
                                                                                                                    fs="italic"
                                                                                                                >
                                                                                                                    either{" "}
                                                                                                                </Text>
                                                                                                                term.
                                                                                                            </Text>
                                                                                                        </List.Item>
                                                                                                    </List>
                                                                                                </Stack>
                                                                                            }
                                                                                        >
                                                                                            <Icon
                                                                                                icon={faQuestionCircle}
                                                                                                size="sm"
                                                                                            />
                                                                                        </Tooltip>
                                                                                    )
                                                                                }
                                                                            />
                                                                        </Input.Wrapper>
                                                                    </Grid.Col>
                                                                    <Grid.Col
                                                                        className={styles["row-group-item"]}
                                                                        span={3}
                                                                    >
                                                                        <Select
                                                                            data-mantine-popover-ignore
                                                                            variant="filled"
                                                                            placeholder="Select option"
                                                                            items={sourceOptions}
                                                                            description="message type"
                                                                            getLabel={(item) => item.label}
                                                                            getValue={(item) => item.value}
                                                                            isSingleSelect={false}
                                                                            withinPortal={false}
                                                                            onSelect={(value) => {
                                                                                setFieldValue(
                                                                                    `${name}.${index}.source`,
                                                                                    value
                                                                                );
                                                                            }}
                                                                            selectedValues={option.source}
                                                                        />
                                                                    </Grid.Col>
                                                                    <Grid.Col
                                                                        className={styles["row-group-item"]}
                                                                        span={3}
                                                                    >
                                                                        <SelectSimilarity
                                                                            inputVariant="filled"
                                                                            withinPortal={false}
                                                                            disabledItems={similarValuesDisabled}
                                                                            onSelect={(value) => {
                                                                                setFieldValue(
                                                                                    `${name}.${index}.similarityThreshold`,
                                                                                    value
                                                                                );
                                                                            }}
                                                                            name={`${name}.${index}.similarityThreshold`}
                                                                        />
                                                                    </Grid.Col>
                                                                    {index > 0 && (
                                                                        <Grid.Col
                                                                            className={styles["row-group-item"]}
                                                                            span={1}
                                                                        >
                                                                            <ActionIcon
                                                                                variant="subtle"
                                                                                className={
                                                                                    styles["remove-option-button"]
                                                                                }
                                                                                size="lg"
                                                                                aria-label="Remove"
                                                                                color="red"
                                                                                onClick={() => remove(index)}
                                                                                disabled={values[name].length === 1}
                                                                            >
                                                                                <Icon icon={faTrash} size="sm" />
                                                                            </ActionIcon>
                                                                        </Grid.Col>
                                                                    )}
                                                                </Grid>
                                                            </Stack>
                                                        )
                                                    )}

                                                    <Button
                                                        variant="subtle"
                                                        color="var(--mantine-color-neutral-9)"
                                                        className={styles["new-option-button"]}
                                                        size="sm"
                                                        icon={faPlus}
                                                        type="button"
                                                        onClick={() => push({ ...EMPTY_FIELD_GROUP[0] })}
                                                        disabled={values[name].length >= 5}
                                                    >
                                                        New Option {values[name].length >= 5 ? "(Max 5)" : ""}
                                                    </Button>
                                                </>
                                            )}
                                        </FieldArray>
                                    </Content.Body>
                                    <Content.Footer bd={0}>
                                        <Group align="center" justify="space-between">
                                            <Group gap="lg">
                                                <Button
                                                    variant="white"
                                                    color="red"
                                                    size="sm"
                                                    type="button"
                                                    onClick={() => {
                                                        resetForm({ values: { [name]: EMPTY_FIELD_GROUP } });
                                                        validateForm({ [name]: EMPTY_FIELD_GROUP });
                                                        onReset();
                                                    }}
                                                >
                                                    Reset
                                                </Button>
                                                {!hasExactSimilarity && hasLimitedDateRange && (
                                                    <Group gap="sm">
                                                        <Icon
                                                            icon={faTriangleExclamation}
                                                            size="sm"
                                                            color="var(--mantine-color-warning-filled)"
                                                        />
                                                        <Text size="sm">
                                                            Search is limited to last 30 days for non-exact word matches
                                                        </Text>
                                                    </Group>
                                                )}
                                            </Group>
                                            <Group gap="sm">
                                                <Button
                                                    variant="default"
                                                    color="gray"
                                                    size="sm"
                                                    type="button"
                                                    onClick={() => setOpened(false)}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    disabled={!isValid}
                                                    variant="filled"
                                                    color="blue"
                                                    component="button"
                                                    size="sm"
                                                    type="submit"
                                                >
                                                    Done
                                                </Button>
                                            </Group>
                                        </Group>
                                    </Content.Footer>
                                </Content>
                            </Form>
                        );
                    }}
                </Formik>
            </Popover.Dropdown>
        </Popover>
    );
};

export { PhraseSearch };
